import {useEffect, useState} from "react";
import ITalent from "../core/interfaces/ITalent";
import getProfilePicture from "../core/utils/getProfilePicture";
import updateCompanyPackageTalentStatusRequest from "../core/api/requests/companies/package/updateCompanyPackageTalentStatusRequest";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import navigateTo from "../core/utils/navigateTo.ts";
import convertToFraction from "../core/utils/convertToFraction";
import { useIntl } from "react-intl";
import convertToAge from "../core/utils/convertToAge";
import moment from 'moment/moment'
import IReducedEvent from "../core/interfaces/IReducedEvent";
import { RootState } from "../core/store/store.ts";
import ERoutes from "../core/enums/ERoutes.ts";

export interface IComponent {
  talent: ITalent;
  companyId?:string
  packageId?:string
  selected?:boolean
  showDetails?: boolean
  allowSelect?: boolean
  onSelect?: () => void
  out?: IReducedEvent[]
}

export default function TalentCard(props: IComponent) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const intl = useIntl()
  const futils = useSelector((state: RootState) => state.frontendUtilities)
  const [ imageUri, setImageUri ] = useState<string>('')
  const [ statusSelection, setStatusSelection] = useState<string>('')

  const getEventColor = (type: string): string => {
    const t = futils?.futils?.events?.types?.find((t: any) => t.name === type)
    return t?.color || '#C7C5C5'
  }

  useEffect(() => {
    const u = getProfilePicture(props.talent)
    setImageUri(u)
    setStatusSelection(props.talent.status || 'pending')
  }, [props.talent]);

  const redirectToTalent = (e?: React.MouseEvent) => {
    if(props.talent.id){
        navigateTo(e, navigate, `/talents/${props.talent.id}`)
    }
  }

  const changeStatus = async () => {
    if(props.companyId && props.packageId && props.talent.id){
      await updateCompanyPackageTalentStatusRequest(dispatch,
        props.companyId,
        props.packageId,
        props.talent.id,
        statusSelection,
        '',
      );
    }
  }
  const [ TO, setTO ] = useState<any>(null)
  useEffect(() => {
    if (TO) {
      clearTimeout(TO)
    }

    setTO(setTimeout(() => {
      changeStatus()
    }, 500))
  }, [statusSelection]);

  const {measurements, characteristics} = props.talent?.talent?.physical || {}
  const { personal, contact } = props.talent?.talent || {}


  return <div className={'talent-card col'}>
    <div className={`row mb-8`}>
        <div className={'col-3'}>
            <div className={'talent-cover'} style={{backgroundImage: `url(${imageUri})`}} onClick={(e) => redirectToTalent(e)}></div>  
        </div>
        <div className={'talent-info col-9 d-flex flex-column'}>
            <div className="talent-card-header d-flex justify-content-between align-items-center pb-2">
                <div className="talent-heading d-flex align-items-center">
                    <span className="talent-name text-uppercase fw-bold fs-4" onClick={(e) => redirectToTalent(e)}>{props.talent.name} {props.talent.lastName}</span>
                    {props.out && props.out.length > 0 &&
                        <div className="position-relative d-inline-flex align-items-center ms-2 talent-out-indicator">
                            <i className="fa-solid fa-triangle-exclamation text-warning fs-5"></i>
                            <div className="talent-out-popover position-absolute top-100 start-50 translate-middle-x mt-1">
                                <div className="bg-body rounded shadow-sm border p-3" style={{minWidth: '250px'}}>
                                    <div className="fw-bold text-muted fs-8 mb-2">{intl.formatMessage({ id: 'LABELS.TALENT_OUT' })}</div>
                                    {props.out.map((event) => (
                                        <a
                                            key={event.id}
                                            href="#"
                                            className="d-flex align-items-center py-1 text-hover-primary"
                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigateTo(e, navigate, ERoutes.BOOKING_INFORMATION.replace(':id', event.id)) }}
                                        >
                                            <span className="bullet bullet-dot me-2" style={{backgroundColor: getEventColor(event.type)}}></span>
                                            <div className="d-flex flex-column">
                                                <span className="fs-7 fw-semibold text-gray-800">{event.name}</span>
                                                <span className="fs-8 text-muted">{moment(event.startDate).format('DD/MM')} - {moment(event.endDate).format('DD/MM')}</span>
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>
                    }
                    {props.talent?.talent?.socialMedia?.socialMedia && 
                        <a className="btn btn-link btn-color-gray-800 btn-active-color-primary ms-3 py-0" target="_blank" href={`https://www.instagram.com/${props.talent?.talent?.socialMedia?.socialMedia[0].userName}`}>
                            <span className="social-icon fa-brands fa-instagram "></span>
                        </a>
                    }
                </div>
                {props?.allowSelect && 
                    <>
                        {props.selected ? 
                            <button className="btn btn-primary btn-active-light border btn-sm" onClick={props?.onSelect}>
                                <i className="fa-solid fa-check"></i>
                                {intl.formatMessage({id: 'PLACEHOLDERS.SELECTED'})}
                            </button>  
                        :
                            <button className="btn btn-light-dark btn-active-primary border btn-sm talent-card-select-btn" onClick={props?.onSelect}>
                                <i className="fa-solid fa-plus"></i>
                                {intl.formatMessage({id: 'PLACEHOLDERS.SELECT'})}
                            </button>  
                        }
                    </>
                }
            </div>
            <div className="talent-details flex-grow-1 bg-light p-5">
                <div className="row">
                    <div className="col-12 col-lg-7">
                        <div className="detail-element mb-1">
                            <span className="detail-title text-muted mt-1 fs-7">{intl.formatMessage({ id: 'PLACEHOLDERS.AGE' })}</span>
                            <span className="detail-value ms-2">
                                {personal?.birthDate 
                                ? `${convertToAge(personal.birthDate)} ${intl.formatMessage({ id: 'COMMON.YEARS' })} (${moment(personal.birthDate).format('MMM DD, YYYY')})` 
                                : ''}
                            </span>
                        </div> 
                        <div className="detail-element mb-1">
                            <span className="detail-title text-muted mt-1 fs-7">{intl.formatMessage({ id: 'PLACEHOLDERS.PHONE' })}</span>
                            <span className="detail-value ms-2">{contact?.phone ?? ''}</span>
                        </div> 
                        {(contact?.address?.fullAddress || contact?.address?.province || contact?.address?.country) && (
                            <div className="detail-element mb-1">
                                <span className="detail-title text-muted mt-1 fs-7">{intl.formatMessage({ id: 'PLACEHOLDERS.ADDRESS' })} 1</span>
                                <span className="detail-value ms-2">{contact.address?.fullAddress || [contact.address?.province, contact.address?.country].filter(Boolean).join(', ')}</span>
                            </div>
                        )}
                        {(contact?.secondaryAddress?.fullAddress || contact?.secondaryAddress?.province || contact?.secondaryAddress?.country) && (
                            <div className="detail-element mb-1">
                                <span className="detail-title text-muted mt-1 fs-7">{intl.formatMessage({ id: 'PLACEHOLDERS.ADDRESS' })} 2</span>
                                <span className="detail-value ms-2">{contact.secondaryAddress?.fullAddress || [contact.secondaryAddress?.province, contact.secondaryAddress?.country].filter(Boolean).join(', ')}</span>
                            </div>
                        )}
                        {(contact?.tertiaryAddress?.fullAddress || contact?.tertiaryAddress?.province || contact?.tertiaryAddress?.country) && (
                            <div className="detail-element mb-1">
                                <span className="detail-title text-muted mt-1 fs-7">{intl.formatMessage({ id: 'PLACEHOLDERS.ADDRESS' })} 3</span>
                                <span className="detail-value ms-2">{contact.tertiaryAddress?.fullAddress || [contact.tertiaryAddress?.province, contact.tertiaryAddress?.country].filter(Boolean).join(', ')}</span>
                            </div>
                        )}
                        <div className="detail-element mb-1">
                            <span className="detail-title text-muted mt-1 fs-7">{intl.formatMessage({ id: 'PLACEHOLDERS.ETHNICITIES' })}</span>
                            <span className="detail-value ms-2">{personal?.ethnicity ? personal.ethnicity.join(', ') : ''}</span>
                        </div> 
                        {characteristics?.dressSize &&
                            <div className="detail-element mb-1">
                                <span className="detail-title text-muted mt-1 fs-7">{intl.formatMessage({ id: 'PLACEHOLDERS.DRESS_SIZE' })}</span>
                                <span className="detail-value ms-2">{characteristics.dressSize} {characteristics?.dressSizeMeasurementUnit ?? ''}</span>
                            </div> 
                        }
                        {characteristics?.shoesSize &&
                            <div className="detail-element mb-1">
                                <span className="detail-title text-muted mt-1 fs-7">{intl.formatMessage({ id: 'PLACEHOLDERS.SHOES_SIZE' })}</span>
                                <span className="detail-value ms-2">{characteristics.shoesSize} {characteristics?.shoesSizeMeasurementUnit ?? ''}</span>
                            </div>
                        }
                    </div>
                    <div className="col-12 col-lg-5">
                        {measurements?.height && 
                            <div className="detail-element mb-1">
                                <span className="detail-title text-muted mt-1 fs-7">{intl.formatMessage({ id: 'PLACEHOLDERS.HEIGHT' })}</span>
                                <span className="detail-value ms-2">{`${measurements.height}cm / ${convertToFraction(measurements.height)}`}</span>
                            </div>          
                        }
                        {measurements?.bust && 
                            <div className="detail-element mb-1">
                                <span className="detail-title text-muted mt-1 fs-7">{intl.formatMessage({ id: 'PLACEHOLDERS.BUST' })}</span>
                                <span className="detail-value ms-2">{`${measurements.bust}cm / ${convertToFraction(measurements.bust)}`}</span>
                            </div>          
                        }
                        {measurements?.waist && 
                            <div className="detail-element mb-1">
                                <span className="detail-title text-muted mt-1 fs-7">{intl.formatMessage({ id: 'PLACEHOLDERS.WAIST' })}</span>
                                <span className="detail-value ms-2">{`${measurements.waist}cm / ${convertToFraction(measurements.waist)}`}</span>
                            </div>          
                        }
                        {measurements?.hip && 
                            <div className="detail-element mb-1">
                                <span className="detail-title text-muted mt-1 fs-7">{intl.formatMessage({ id: 'PLACEHOLDERS.HIP' })}</span>
                                <span className="detail-value ms-2">{`${measurements.hip}cm / ${convertToFraction(measurements.hip)}`}</span>
                            </div>          
                        }
                        {characteristics?.hairColor &&
                            <div className="detail-element mb-1">
                                <span className="detail-title text-muted mt-1 fs-7">{intl.formatMessage({ id: 'PLACEHOLDERS.HAIR_COLOR' })}</span>
                                <span className="detail-value ms-2">{characteristics.hairColor}</span>
                            </div>
                        }
                        {characteristics?.eyesColor &&
                            <div className="detail-element mb-1">
                                <span className="detail-title text-muted mt-1 fs-7">{intl.formatMessage({ id: 'PLACEHOLDERS.EYES_COLOR' })}</span>
                                <span className="detail-value ms-2 text-capitalize">{characteristics.eyesColor}</span>
                            </div>
                        }
                    </div>
                </div>
            </div>
        </div>
    </div>
    <style>{`
      [data-bs-theme="dark"] .talent-card-select-btn,
      .theme-dark .talent-card-select-btn {
        color: rgba(255,255,255,0.85) !important;
        border-color: rgba(255,255,255,0.35) !important;
      }
      [data-bs-theme="dark"] .talent-card-select-btn .fa-plus,
      .theme-dark .talent-card-select-btn .fa-plus {
        color: inherit;
      }
      .talent-out-popover { display: none; z-index: 10; white-space: nowrap; }
      .talent-out-indicator:hover .talent-out-popover { display: block; }
    `}</style>
  </div>
}
